import { Deferred } from "./Deferred";
import { log } from "./log";
import throttle from "lodash/throttle";

export type FindElementReason = "dom_not_found" | "dom_found_but_size_zero" | null;

export type FindVisibleElementResult = {
  element: Element | null;
  reason: FindElementReason;
};

export const findVisibleElement = (selector: string): FindVisibleElementResult => {
  const el = findElement(selector);
  if (!el) return { element: null, reason: "dom_not_found" };

  const rect = el.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return { element: null, reason: "dom_found_but_size_zero" };

  return { element: el, reason: null };
};

// Helper function to find element within a specific document context
function _findElementInContext(selector: string, contextDocument: Document): Element | null {
  try {
    if (isXPathSelector(selector)) {
      return contextDocument.evaluate(selector, contextDocument, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null)
        .singleNodeValue as Element | null;
    } else {
      return contextDocument.querySelector(selector);
    }
  } catch (e) {
    log(`Error querying selector "${selector}" in a specific document context:`, e);
    return null;
  }
}

export const findElement = (selector: string): Element | null => {
  try {
    if (!selector) return null;

    let element = _findElementInContext(selector, document);
    if (element) {
      return element;
    }

    const iframes = document.querySelectorAll("iframe");
    for (const iframe of iframes) {
      try {
        const iframeDocument = iframe.contentDocument;
        if (iframeDocument) {
          element = _findElementInContext(selector, iframeDocument);
          if (element) {
            return element;
          }
        }
      } catch (e) {
        log(`Could not access iframe content for selector "${selector}" due to security restrictions.`);
      }
    }

    return null;
  } catch (e) {
    log(`General error finding element with selector "${selector}":`, e);
    return null;
  }
};

function isXPathSelector(expression: string) {
  try {
    return (
      (expression.slice(0, 2) === "//" || expression.slice(0, 3) === "(//") &&
      document.evaluate(expression, document, null, XPathResult.ANY_TYPE, null) !== null
    );
  } catch (error) {
    return false;
  }
}

export class ElementClicker {
  elementTracker: ElementTracker = new ElementTracker();
  isClicked = false;

  async clickElement(selector: string) {
    await this.elementTracker.trackElement(selector, (result: FindVisibleElementResult) => {
      if (!this.isClicked) {
        this.isClicked = true;
        if (result.element) {
          (result.element as HTMLElement).click();
        }
        this.elementTracker.stop();
      }
    });
  }
}

export class ElementTextWriter {
  startedTyping: boolean = false;

  async writeText(inputEl: HTMLElement, text: string, instantWrite = false) {
    let writeDelay = 0;
    if (this.startedTyping) {
      return;
    }
    inputEl.focus();
    this.startedTyping = true;
    if (instantWrite) {
      await this.writeLetter(inputEl, text, 0, 0);
      this.startedTyping = false;
      return;
    }
    await this.writeLetter(inputEl, text, 0, this.calculateDelay(text));
    this.startedTyping = false;
  }

  private writeLetter(el: HTMLElement, text: string, position: number, delay: number): Promise<void> {
    return new Promise<void>((resolve) => {
      if (position >= text.length) {
        resolve();
        return;
      }

      setTimeout(() => {
        const currentText = text.slice(0, position + 1);

        if (this.isInputElement(el)) {
          // Handle input/textarea elements
          const event = new Event("input", { bubbles: true });
          const nativeInputValueSetter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(el), "value")?.set;
          nativeInputValueSetter?.call(el, currentText);
          el.dispatchEvent(event);
        } else if (this.isContentEditableElement(el)) {
          // Handle contenteditable elements
          this.insertTextInContentEditable(el, currentText);
        }

        this.writeLetter(el, text, position + 1, delay).then(resolve);
      }, delay);
    });
  }

  private isInputElement(el: HTMLElement): el is HTMLInputElement | HTMLTextAreaElement {
    return el.tagName === "INPUT" || el.tagName === "TEXTAREA";
  }

  private isContentEditableElement(el: HTMLElement): boolean {
    return el.contentEditable === "true" || el.getAttribute("contenteditable") === "true";
  }

  private insertTextInContentEditable(el: HTMLElement, text: string): void {
    try {
      // Clear existing content first
      el.innerHTML = "";

      // For ProseMirror/TipTap editors, we need to maintain the paragraph structure
      if (el.classList.contains("ProseMirror") || el.classList.contains("tiptap")) {
        const p = document.createElement("p");
        p.textContent = text;
        el.appendChild(p);
      } else {
        // For regular contenteditable elements, just set text content
        el.textContent = text;
      }

      // Place cursor at the end
      const selection = window.getSelection();
      if (selection) {
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }

      // Dispatch input event to notify frameworks
      const inputEvent = new Event("input", { bubbles: true });
      el.dispatchEvent(inputEvent);
    } catch (e) {
      log("Error inserting text in contenteditable element:", e);
      // Fallback to simple text insertion
      el.textContent = text;
    }
  }

  private calculateDelay(text: string, minDelay = 20, baseDelay = 200, scalingFactor = 50) {
    let delay = baseDelay - Math.log(text.length + 1) * scalingFactor;
    delay = Math.max(minDelay, delay);
    return delay;
  }
}

export class ElementTracker {
  elementSearcher: ElementSearcher = new ElementSearcher();
  disconnectObserver: Function | null = null;
  stopped: boolean = false;

  static readonly initialSearchTimeout = 1000;

  trackElement = async (
    selector: string,
    callback: (result: FindVisibleElementResult) => void,
    timeout = ElementTracker.initialSearchTimeout
  ) => {
    let lastResult: FindVisibleElementResult | undefined = undefined;

    const observer = new MutationObserver((mutations) => {
      const result = findVisibleElement(selector);
      if (result.element !== lastResult?.element || result.reason !== lastResult?.reason) {
        lastResult = result;
        callback(result);
      }
    });

    this.disconnectObserver = () => {
      observer.disconnect();
      log("stopped observing", selector);
    };

    log("initial waitForElement", selector);
    const initialResult = await this.elementSearcher.waitForElement(selector, timeout);
    callback(initialResult);
    lastResult = initialResult;

    if (this.stopped) {
      log("stopped tracking element, will not observe", selector);
      return;
    }

    observer.observe(document, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });
  };

  trackElements = async (selectors: string[], callback: (els: Record<string, FindVisibleElementResult>) => void) => {
    let lastElements: Record<string, FindVisibleElementResult> = {};

    const observer = new MutationObserver((mutations) => {
      const els = selectors.reduce(
        (acc, selector) => {
          acc[selector] = findVisibleElement(selector);
          return acc;
        },
        {} as Record<string, FindVisibleElementResult>
      );

      const hasChanges = selectors.some((selector) => {
        const current = els[selector];
        const last = lastElements[selector];
        return !last || current.element !== last.element || current.reason !== last.reason;
      });
      if (hasChanges) {
        lastElements = els;
        callback(els);
      }
    });

    this.disconnectObserver = () => {
      observer.disconnect();
      log("stopped observing", selectors);
    };

    const resultsArray = await Promise.all(
      selectors.map((selector) => this.elementSearcher.waitForElement(selector, ElementTracker.initialSearchTimeout))
    );

    const elementsRecord = selectors.reduce(
      (acc, selector, index) => {
        acc[selector] = resultsArray[index];
        return acc;
      },
      {} as Record<string, FindVisibleElementResult>
    );

    callback(elementsRecord);
    lastElements = elementsRecord;

    if (this.stopped) {
      log("stopped tracking elements, will not observe", selectors);
      return;
    }

    observer.observe(document, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });
  };

  stop = () => {
    this.stopped = true;
    this.elementSearcher.cancelAll();
    this.disconnectObserver?.();
  };
}

export class ElementSearcher {
  cancelled: boolean;

  constructor() {
    this.cancelled = false;
  }

  cancelAll() {
    this.cancelled = true;
  }

  waitForElement = async (selector: string, timeout?: number): Promise<FindVisibleElementResult> => {
    const findThisElement = (): FindVisibleElementResult => findVisibleElement(selector);

    // check if element is already available
    const initialCheckResult = findThisElement();
    if (initialCheckResult.element) {
      log("element already available from first try", selector);
      return Promise.resolve(initialCheckResult);
    }

    const defferedFind = new Deferred<FindVisibleElementResult>();

    const check = throttle(() => {
      if (this.cancelled) {
        log("cancelled waitForElement", selector);
        defferedFind.resolve({ element: null, reason: "dom_not_found" });
        return;
      }

      const currentCheckResult = findThisElement();
      if (currentCheckResult.element) {
        log("found element", selector);
        defferedFind.resolve(currentCheckResult);
      }
    }, 100);

    const observer = new MutationObserver(() => {
      check();
    });

    observer.observe(document, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });

    const intervalId = setInterval(() => {
      check();
    }, 1000);

    let timeoutId: any;
    if (timeout) {
      timeoutId = setTimeout(() => {
        log("wait for selector timeout triggered", timeout);
        defferedFind.resolve(findThisElement());
      }, timeout);
    }

    // wait for deffered to resolve
    const result = await defferedFind.promise;
    log("wait result", result);

    // cleanup
    observer.disconnect();
    clearInterval(intervalId);
    clearTimeout(timeoutId);

    return result;
  };

  waitForElementNotPresent = async (selector: string, timeout?: number): Promise<boolean> => {
    const findThisElement = (): FindVisibleElementResult => findVisibleElement(selector);

    const defferedFind = new Deferred<boolean>();

    const check = throttle(() => {
      if (this.cancelled) {
        log("cancelled waitForElementNotPresent", selector);
        defferedFind.resolve(false);
        return;
      }

      const currentCheckResult = findThisElement();
      if (currentCheckResult.element) {
        log("element found during waitForElementNotPresent - failing", selector);
        defferedFind.resolve(false);
      }
    }, 100);

    const observer = new MutationObserver(() => {
      check();
    });

    observer.observe(document, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });

    const intervalId = setInterval(() => {
      check();
    }, 1000);

    // For waitForElementNotPresent, we always wait for the full timeout
    // If no timeout is specified, we use a default
    const actualTimeout = timeout ?? 3000;
    const timeoutId = setTimeout(() => {
      log("waitForElementNotPresent timeout reached - element stayed absent", selector);
      defferedFind.resolve(true);
    }, actualTimeout);

    // Do an initial check to see if element is already present
    check();

    // wait for deffered to resolve
    const result = await defferedFind.promise;
    log("wait not present result", result);

    // cleanup
    observer.disconnect();
    clearInterval(intervalId);
    clearTimeout(timeoutId);

    return result;
  };
}
